"use client";

import { useTranslations } from "next-intl";
import React, { useEffect, useRef } from "react";
import { cn } from "../../helpers/classNames";
import { pushDataLayer } from "../../helpers/dataLayer";
import { getWindow } from "../../helpers/getWindow";
import type { DataLayerItemPageTrack, OverlayType } from "../../helpers/types";
import { useCountry } from "../../hooks/useCountry";
import { useIsMobileSafari } from "../../hooks/useIsMobileSafari";
import { useIsPersistingForbidden } from "../../hooks/useIsPersistingForbidden";
import { useReloadAds, useStickyAdOffsets } from "../Ad/hooks";
import { ClickPerformanceSticky } from "../Ad/slots/ClickPerformanceSticky";
import { Button } from "../Button";
import { useIsPay } from "../Consent/hooks";
import { ErrorBoundary } from "../ErrorBoundary";
import { useIsWebview } from "../WebviewContext/hooks";
import styles from "./index.module.scss";
import type { OverlaySetTargeting } from "./types";

const zIndexMap = {
    cmp: "--wcom-z-cmp",
    lightbox: "--wcom-z-lightbox",
    newDataUpdate: "--wcom-z-newDataUpdate",
    notifications: "--wcom-z-notifications",
    menu: "--wcom-z-menu",
};

const closeIconColorMap = {
    white: "--wcom-c-white",
    blueDark: "--wcom-c-blueDark",
    almostBlack: "--wcom-c-almostBlack",
};

const backdropColorMap = {
    blackAlpha80: "--wcom-c-blackAlpha80",
    blackAlpha90: "--wcom-c-blackAlpha90",
    blueDarkAlpha80: "--wcom-c-blueDarkAlpha80",
    white: "--wcom-c-white",
};

type TrackingType = "open" | "close" | "openAndClose" | "none";

type LightboxProps = {
    callback?: () => void;
    children: React.ReactNode;
    isFullscreen?: boolean;
    zIndexKey?: keyof typeof zIndexMap;
    closeIconColor?: keyof typeof closeIconColorMap;
    backdropColor?: keyof typeof backdropColorMap;
    hasBackdropBlur?: boolean;
    trackPageImpression: TrackingType;
    trackOverlay?: TrackingType;
    triggerAdReload: TrackingType;
    overlaySetTargeting?: OverlaySetTargeting;
    overlayType?: OverlayType;
    additionalTrackingParams?: Pick<
        DataLayerItemPageTrack,
        "pageType" | "pageTypeDetail" | "pageName"
    >;
};

export const Lightbox: React.FC<LightboxProps> = ({
    children,
    callback,
    isFullscreen,
    zIndexKey = "lightbox",
    closeIconColor = "white",
    backdropColor = "blueDarkAlpha80",
    hasBackdropBlur = true,
    trackPageImpression,
    trackOverlay,
    triggerAdReload,
    overlaySetTargeting,
    overlayType,
    additionalTrackingParams,
}) => {
    const country = useCountry();
    const window = getWindow();
    const reloadAds = useReloadAds();
    const hasPIOnOpen =
        trackPageImpression === "open" ||
        trackPageImpression === "openAndClose";
    const { bottom, top } = useStickyAdOffsets();
    const t = useTranslations("component.lightbox");
    const ref = useRef<HTMLDivElement>(null);
    const isPersistingForbidden = useIsPersistingForbidden();
    const isMobileSafari = useIsMobileSafari();
    const isPay = useIsPay();
    const isWebview = useIsWebview();

    // Focus the dialog when it opens
    useEffect(() => {
        ref.current?.focus();
    }, []);

    // setup+teardown useEffect, don't add any other logic here nor effect deps
    useEffect(() => {
        // >>> open lightbox <<<
        if (window) {
            window.document.body.classList.add("noscroll");
        }
        if (overlayType) {
            if (hasPIOnOpen) {
                pushDataLayer({
                    type: "pageTrack",
                    event: "pagetrack",
                    overlay: "open",
                    overlayType,
                    ...additionalTrackingParams,
                });
            } else if (
                trackOverlay === "open" ||
                trackOverlay === "openAndClose"
            ) {
                pushDataLayer({
                    type: "overlay",
                    event: "overlay",
                    overlay: "open",
                    overlayType,
                });
            }
            // else no dl tracking at all
        }

        // >>> close lightbox <<<
        return (): void => {
            if (window) {
                window.document.body.classList.remove("noscroll");
            }
            if (overlayType) {
                if (
                    trackPageImpression === "close" ||
                    trackPageImpression === "openAndClose"
                ) {
                    pushDataLayer({
                        type: "pageTrack",
                        event: "pagetrack",
                        overlay: "close",
                        overlayType,
                        ...additionalTrackingParams,
                    });
                } else if (
                    trackOverlay === "close" ||
                    trackOverlay === "openAndClose"
                ) {
                    pushDataLayer({
                        type: "overlay",
                        event: "overlay",
                        overlay: "close",
                        overlayType,
                    });
                }
                // else no dl tracking at all
            }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (hasPIOnOpen) {
            const overlaySlot = window?.adHandler?.service?.getAdUnit(
                `WET_${country.toUpperCase()}_M_overlay_stickybanner`
            );
            if (overlaySlot) {
                overlaySlot.enable("adUnit");
            }
        }

        if (overlaySetTargeting) {
            // this can sometimes fail due to timing, so we also have the setTargeting
            // with data-programmatic-component and data-programmatic-type in place.
            window?.adHandler?.service?.setTargeting(
                overlaySetTargeting,
                "open"
            );
        }

        if (
            reloadAds &&
            (triggerAdReload === "open" || triggerAdReload === "openAndClose")
        ) {
            reloadAds({
                de: ["WET_DE_M_overlay_performancerectangle1"],
                at: undefined,
                ch: ["WET_CH_M_overlay_performancerectangle1"],
            });
        }

        return (): void => {
            const overlaySlot = window?.adHandler?.service?.getAdUnit(
                `WET_${country.toUpperCase()}_M_overlay_stickybanner`
            );
            if (overlaySlot) {
                overlaySlot.disable("adUnit");
            }

            if (
                reloadAds &&
                (triggerAdReload === "close" ||
                    triggerAdReload === "openAndClose")
            ) {
                if (overlaySetTargeting) {
                    window?.adHandler?.service?.setTargeting(
                        overlaySetTargeting,
                        "close"
                    );
                }
                reloadAds();
            }
        };
    }, [
        country,
        overlaySetTargeting,
        hasPIOnOpen,
        reloadAds,
        triggerAdReload,
        window?.adHandler?.service,
    ]);

    let topMargin = isFullscreen ? "0" : top;
    let bottomMargin = isFullscreen ? "0" : bottom;
    if (
        country === "at" &&
        overlayType === "vhs" &&
        !isFullscreen &&
        !isWebview &&
        !isPay &&
        !isPersistingForbidden
    ) {
        if (isMobileSafari) {
            topMargin = "125px";
        } else {
            bottomMargin = "125px";
        }
    }

    return (
        <>
            <div
                ref={ref}
                className={styles.wrapper}
                role="dialog"
                style={{
                    zIndex: `calc(var(${zIndexMap[zIndexKey]}) - 10)`,
                    top: topMargin,
                    bottom: bottomMargin,
                }}
                data-testid="backdropElement"
                data-programmatic-component="overlay"
                data-programmatic-type={overlaySetTargeting}
                aria-label={t("dialog")}
                // focus in useEffect only works with tabIndex="-1"
                tabIndex={-1}
            >
                {callback !== undefined && (
                    <Button
                        variant="unstyled"
                        className={styles.close}
                        onClick={callback}
                        style={{
                            color: `var(${closeIconColorMap[closeIconColor]})`,
                            zIndex: `calc(var(${zIndexMap[zIndexKey]}) + 1)`,
                        }}
                    >
                        <span className="icon-cross" />
                    </Button>
                )}
                <ErrorBoundary>
                    <div
                        style={{
                            zIndex: `var(${zIndexMap[zIndexKey]})`,
                        }}
                    >
                        {children}
                    </div>
                </ErrorBoundary>
                <Button
                    variant="unstyled"
                    className={cn(
                        styles.backdrop,
                        hasBackdropBlur && styles.backdropBlur
                    )}
                    aria-label={t("backdrop")}
                    style={{
                        background: `var(${backdropColorMap[backdropColor]})`,
                    }}
                    onClick={(): void => {
                        if (callback !== undefined) {
                            callback();
                        }
                    }}
                />
            </div>
            {hasPIOnOpen ? (
                <ClickPerformanceSticky
                    slot={{
                        de: "WET_DE_M_overlay_stickybanner",
                        at: "WET_AT_M_overlay_stickybanner",
                        ch: "WET_CH_M_overlay_stickybanner",
                    }}
                />
            ) : null}
        </>
    );
};
